import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquareText } from "lucide-react";
import { toast } from "sonner";

export function SmsButton({ recipients = [], defaultMessage = "", label = "Invia SMS", variant = "outline", size, className = "", context, bulk = false }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  const list = (Array.isArray(recipients) ? recipients : [recipients]).filter(Boolean);
  const valid = list.filter((r) => String(r.number || "").replace(/\D/g, "").length >= 9);
  const numbers = valid.map((r) => r.number);
  const disabled = valid.length === 0;
  const segments = Math.max(1, Math.ceil((msg.length || 0) / 160));

  const send = async () => {
    if (!msg.trim()) { toast.error("Scrivi un messaggio"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/sms/send", { recipients: numbers, message: msg, context });
      toast.success(`${data.sent} SMS inviati${data.invalid?.length ? ` · ${data.invalid.length} numeri non validi` : ""}${data.remaining_credits != null ? ` · crediti residui: ${data.remaining_credits}` : ""}`);
      setOpen(false); setMsg(defaultMessage);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio SMS");
    } finally { setSending(false); }
  };

  return (
    <>
      <Button variant={variant} size={size} className={className} disabled={disabled} data-testid="sms-open-btn"
        title={disabled ? "Nessun cellulare valido" : label}
        onClick={(e) => { e.stopPropagation(); setMsg(defaultMessage); setOpen(true); }}>
        <MessageSquareText className="h-4 w-4 mr-2" />{label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()} data-testid="sms-dialog">
          <DialogHeader><DialogTitle>{bulk ? `Invia SMS a ${valid.length} destinatari` : "Invia SMS"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            {!bulk && valid[0] && (
              <p className="text-sm text-muted-foreground">A: <span className="font-medium text-foreground">{valid[0].name || valid[0].number}</span> · {valid[0].number}</p>
            )}
            {bulk && (
              <p className="text-xs text-muted-foreground" data-testid="sms-bulk-info">
                {valid.length} numeri validi{list.length - valid.length > 0 ? ` · ${list.length - valid.length} contatti senza cellulare (esclusi)` : ""}. I duplicati vengono rimossi automaticamente.
              </p>
            )}
            <Textarea data-testid="sms-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} maxLength={640} placeholder="Scrivi il messaggio…" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Mittente: {process.env.REACT_APP_SMS_SENDER || "Avvera LT"}</span>
              <span data-testid="sms-counter">{msg.length} caratteri · {segments} SMS{bulk ? ` × ${valid.length} = ${segments * valid.length}` : ""}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="sms-send-confirm" onClick={send} disabled={sending || disabled}>{sending ? "Invio…" : "Invia SMS"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
